import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  handleUnauthorized,
  showSuccessMessage,
  showErrorMessage,
} from "../../utils/apiHelper";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../quillCustomStyles.css";
import { QuillDeltaToHtmlConverter } from "quill-delta-to-html";

const AddCMSPageNew = ({onTitleLoad}) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(null); // Store Delta
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [charCount, setCharCount] = useState(0);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const authToken = localStorage.getItem("authToken");
  const quillRef = useRef(null);

  useEffect(() => {
    if (!isEditMode) return;

    const fetchCMS = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/admin/cmsById/${id}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );

        if (response.status === 401) {
          handleUnauthorized(navigate);
          return;
        }

        const data = await response.json();
        if (response.ok) {
          setTitle(data.result.title || "");
          // If editing, treat saved HTML as-is for now
          setContent(data.result.content || "");
          setStatus(data.result.status || "");
           if (data?.result?.title && onTitleLoad) {
          onTitleLoad(data.result.title);
        }
        } else {
          setErrorMessage("Error fetching CMS content");
          clearMessage();
        }
      } catch (error) {
        setErrorMessage("Error fetching CMS content");
        clearMessage();
      } finally {
        setLoading(false);
      }
    };

    fetchCMS();
  }, [id, navigate]);

  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!content) {
        newErrors.content = "Content is required";
      } else if (content.length < 20) {
        newErrors.content = "Content must be at least 20 characters";
      }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!validateInputs()) {
      setLoading(false);
      return;
    }

    try {
      const method = isEditMode ? "PUT" : "POST";
      const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/${
        isEditMode ? `cmsUpdate/${id}` : "cmsAdd"
      }`;



      // If using Delta object, convert to inline-style HTML
      const converter = new QuillDeltaToHtmlConverter(content.ops, {
        inlineStyles: true,
        customTag: (op) => {
          if (op.insert && typeof op.insert === "string") {
            const { attributes } = op;
            let style = "";
      
            if (attributes?.bold) style += "font-weight:bold;";
            if (attributes?.italic) style += "font-style:italic;";
            if (attributes?.underline) style += "text-decoration:underline;";
            if (attributes?.strike) style += "text-decoration:line-through;";
      
            if (style) {
              return { tag: "span", attributes: { style } };
            }
          }
          return undefined;
        },
      });
      const htmlContent = converter.convert();
      //console.log('htmlContent',htmlContent);
      

      const response = await fetch(API_ENDPOINT, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          title,
          content: htmlContent,
          status: "active",
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(
          data.message || (isEditMode ? "CMS Updated" : "CMS Added")
        );
        clearMessage();
        setTimeout(() => navigate("/cms"), 3000);
      } else {
        setErrorMessage(data.message || "Something went wrong");
        clearMessage();
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
      clearMessage();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && <SuccessAlert message={successMessage} />}
            {errorMessage && <DengerAlert message={errorMessage} />}

            <form
              className="row gy-3 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              <div className="col-md-12">
                <label className="form-label">Title<span className="text-danger-600">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={title}
                  placeholder="Enter title"
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setErrors((prev) => ({ ...prev, title: "" }));
                  }}
                  readOnly
                />
                {errors.title && (
                  <div className="invalid-feedback">{errors.title}</div>
                )}
              </div>

            <div className="col-md-12">
              <label className="form-label">Content<span className="text-danger-600">*</span></label>
              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={(value, delta, source, editor) => {
                  const deltaContent = editor.getContents();
                  setContent(deltaContent);
                  const plainText = editor.getText();
                  setCharCount(plainText.trim().length);
                  setErrors((prev) => ({ ...prev, content: "" }));
                }}
                modules={modules}
                formats={formats}
              />
              <small className="text-muted d-block mt-1">
                Character count: {charCount}
              </small>
              {errors.content && (
                <div className="invalid-feedback d-block">
                  {errors.content}
                </div>
              )}
            </div>
              <div className="col-12">
                <button
                  className="btn btn-primary mt-3 float-end"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCMSPageNew;